import { Component, Input, OnInit, computed, inject, signal } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { BaseModalComponent, ToleranceMeterSegment } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import {
  CuadrajeColorGroup,
  CuadrajeFabricRow,
  CuadrajeLegendEntry,
  CuadrajeQuantity,
  CuadrajeStatus,
  CuadrajeSummary,
  CuadrajeUnit,
} from '@app/models/production/cuadraje';
import { ProductionCardsProxyService } from '@app/proxies/production/production-card.proxy.service';
import { CuadrajeBalanceoAdapterService } from '@app/services/cuadraje-balanceo-adapter.service';
import { FinishingRoles } from '@app/models/roles/roles-group';

/**
 * Legend colors. Pending and On Hold never reach the bar, so they only have to be legible as a
 * marker; the other two also have to match what the meter paints.
 *
 * Pending uses `--p-text-color` rather than a literal black so its marker stays visible in dark mode.
 */
const PENDING_COLOR = 'var(--p-text-color)';
/** Same red as the meter's `--tm-excess-color`, so bar and legend read as one. */
const EXCESS_COLOR = 'var(--p-red-500)';
const LEFT_OVER_COLOR = 'var(--p-purple-500)';
const ON_HOLD_COLOR = 'var(--p-amber-500)';

/**
 * Left over is drawn faded on the bar: it counts towards the order, but it is not what the client's
 * tolerance is read against, so it should not compete with the Work Order blocks.
 */
const LEFT_OVER_BAR_COLOR = 'color-mix(in srgb, var(--p-purple-500) 35%, transparent)';

@Component({
  selector: 'app-cuadraje-modal',
  templateUrl: './cuadraje-modal.component.html',
  styleUrl: './cuadraje-modal.component.scss',
})
export class CuadrajeModalComponent extends BaseModalComponent<null> implements OnInit {
  private readonly productionCardProxy = inject(ProductionCardsProxyService);
  private readonly adapter = inject(CuadrajeBalanceoAdapterService);

  /** Resolved by the caller before the modal opens — see roll-final-qc.component.ts. */
  @Input() productOrderNumber!: string;
  /** The Work Order's own unit — shown fixed to this without the ChangeUOMCuadraje role. */
  @Input() workOrderUnit: CuadrajeUnit = 'lbs';
  /** Color of the production card the search was made from — brought to the front of the list. */
  @Input() currentColorReference?: string;

  /** Only visible with this role — otherwise the unit stays fixed to the Work Order's own. */
  readonly canChangeUnit = AuthService.hasRole(FinishingRoles.ChangeUOMCuadraje);

  unitOptions: { label: string; value: CuadrajeUnit }[] = [
    { label: 'Lbs', value: 'lbs' },
    { label: 'Yds', value: 'yds' },
  ];

  unit = signal<CuadrajeUnit>('lbs');
  isLoading = signal(0);

  private readonly liveSummary = signal<CuadrajeSummary | null>(null);

  get summary(): CuadrajeSummary | null {
    return this.liveSummary();
  }

  /** The color searched from is pinned first; the rest keep the order the API returned them in. */
  colors = computed<CuadrajeColorGroup[]>(() => {
    const colors = this.liveSummary()?.colors ?? [];
    if (!this.currentColorReference) return colors;

    const currentIndex = colors.findIndex((color) => color.colorReference === this.currentColorReference);
    if (currentIndex <= 0) return colors;

    const reordered = [...colors];
    const [current] = reordered.splice(currentIndex, 1);
    reordered.unshift(current);

    return reordered;
  });

  private incrementLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private decrementLoading(): void {
    this.isLoading.update((v) => Math.max(0, v - 1));
  }

  ngOnInit(): void {
    this.unit.set(this.canChangeUnit ? 'lbs' : this.workOrderUnit);
    this.load();
  }

  /** Opens instantly on an empty modal (per caller) and fills in once this resolves — also what the header's Reload button re-runs. */
  load(): void {
    this.incrementLoading();
    this.productionCardProxy
      .getCustomerPoCuadraje(this.productOrderNumber)
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (response) => {
          if (response.data) {
            this.liveSummary.set(this.adapter.toCuadrajeSummary(response.data));
          }
        },
      });
  }

  onClose(): void {
    this.close({ action: 'close', data: null });
  }

  /** Resolves a quantity to the unit currently selected. */
  amount(quantity: CuadrajeQuantity): number {
    return this.unit() === 'yds' ? quantity.yds : quantity.lbs;
  }

  /** What the Work Orders got approved. The number every other one on the row is read against. */
  approved(row: CuadrajeFabricRow): number {
    return row.contributions.reduce((total, contribution) => total + this.amount(contribution.quantity), 0);
  }

  /** Approved rolls later marked Left Over. Still counts on the bar, apart from the Work Orders. */
  leftOver(row: CuadrajeFabricRow): number {
    return this.amount(row.leftOver);
  }

  /** Rolls that failed Final QC. Never drawn on the bar. */
  onHold(row: CuadrajeFabricRow): number {
    return this.amount(row.onHold);
  }

  /** Still to produce against the Customer PO: Target − Approved Rolls. Never drawn on the bar. */
  pending(row: CuadrajeFabricRow): number {
    return Math.max(0, this.amount(row.target) - this.approved(row));
  }

  /**
   * Approved past the top of the client's tolerance — what they will not take. Measured against the
   * upper limit and not the target, so producing over the target while still inside the accepted
   * range is not flagged. This is the stretch the meter paints red.
   */
  excess(row: CuadrajeFabricRow): number {
    return Math.max(0, this.approved(row) - this.amount(row.toleranceMax));
  }

  /** Everything that sits on the bar: what the Work Orders approved plus the left over. */
  real(row: CuadrajeFabricRow): number {
    return this.approved(row);
  }

  /** How far the approved quantity is from the target, as the Customer PO reads it. */
  approvedPercentage(row: CuadrajeFabricRow): number {
    const target = this.amount(row.target);

    return target > 0 ? (this.approved(row) / target) * 100 : 0;
  }

  status(row: CuadrajeFabricRow): CuadrajeStatus {
    const real = this.real(row);
    if (real < this.amount(row.toleranceMin)) return 'below';
    if (real > this.amount(row.toleranceMax)) return 'above';

    return 'inRange';
  }

  statusLabel(row: CuadrajeFabricRow): string {
    const labels: Record<CuadrajeStatus, string> = {
      below: 'Under range',
      inRange: 'In range',
      above: 'Over range',
    };

    return labels[this.status(row)];
  }

  statusSeverity(row: CuadrajeFabricRow): 'success' | 'warn' | 'danger' {
    const severities: Record<CuadrajeStatus, 'success' | 'warn' | 'danger'> = {
      below: 'warn',
      inRange: 'success',
      above: 'danger',
    };

    return severities[this.status(row)];
  }

  /**
   * One segment per Work Order, plus the left over as a `trailing` one. The meter carves whatever
   * passes `excessFrom` out of the Work Order stack into its own red block, and leaves a trailing
   * segment alone — so the left over keeps its faded purple even when it sits past the limit.
   */
  segments(row: CuadrajeFabricRow): ToleranceMeterSegment[] {
    const segments: ToleranceMeterSegment[] = row.contributions.map((contribution) => ({
      label: contribution.workOrderNumber,
      value: this.amount(contribution.quantity),
      color: contribution.color,
    }));

    const leftOver = this.leftOver(row);
    if (leftOver > 0) {
      segments.push({ label: 'Sobrante', value: leftOver, color: LEFT_OVER_BAR_COLOR, trailing: true });
    }
    console.log('segments', segments);
    return segments;
  }

  /**
   * The bar runs up to the upper tolerance limit, so the dashed limits and the target land where
   * the client's range actually is. Approved past that limit widens the scale to fit, and the left
   * over is added on top because the meter anchors it at the limit rather than at the end of the
   * fill — so the scale has to leave room for it beyond whichever of the two ends further right.
   */
  scaleMax(row: CuadrajeFabricRow): number {
    return Math.max(this.amount(row.toleranceMax), this.approved(row) + this.leftOver(row));
  }

  /** The Work Order blocks of the bar, listed under the item name in the same colors. */
  contributions(row: CuadrajeFabricRow): CuadrajeLegendEntry[] {
    return row.contributions.map((contribution) => ({
      label: contribution.workOrderNumber,
      color: contribution.color,
      value: this.amount(contribution.quantity),
      percentage: this.toPercentage(row, this.amount(contribution.quantity)),
    }));
  }

  /**
   * The legend under the bar. Pendiente and Fuera de Tolerancia are shown even at zero, since
   * reading "nothing left to produce" and "nothing over the limit" is the point of having them there.
   */
  metrics(row: CuadrajeFabricRow): CuadrajeLegendEntry[] {
    return [
      { label: 'Pendiente', color: PENDING_COLOR, value: this.pending(row) },
      { label: 'Fuera de Tolerancia', color: EXCESS_COLOR, value: this.excess(row) },
      { label: 'Sobrante', color: LEFT_OVER_COLOR, value: this.leftOver(row) },
    ].map((entry) => ({ ...entry, percentage: this.toPercentage(row, entry.value) }));
  }

  /** Sits below the rest, split off by a rule, and only when there is something on hold. */
  onHoldMetric(row: CuadrajeFabricRow): CuadrajeLegendEntry | null {
    const onHold = this.onHold(row);
    if (onHold <= 0) return null;

    return {
      label: 'En Espera',
      color: ON_HOLD_COLOR,
      value: onHold,
      percentage: this.toPercentage(row, onHold),
    };
  }

  /** Percentages are read against the target, the same number the header shows. */
  private toPercentage(row: CuadrajeFabricRow, value: number): number {
    const target = this.amount(row.target);

    return target > 0 ? (value / target) * 100 : 0;
  }
}
